import { Controller } from "@hotwired/stimulus"
import noUiSlider from "nouislider"
import { CurrencyHelper } from "~/helpers/currency_helper"

export default class extends Controller {
  static targets = ["slider", "totalCost", "buyBtn", "disabledBuyBtn", "contactSalesBtn", "discount", "discountMessage", "applyDiscountBtn"]

  static values = {
    currentSearches: Number,
    currentVerifications: Number,
    currency: String
  }

  connect() {
    Turbo.cache.exemptPageFromCache() // This is required to prevent multiple sliders on data platform pricing page
    let checkoutURL = new URL(this.buyBtnTarget.href)
    let salesURL = new URL(this.contactSalesBtnTarget.href)

    // Sliders
    this.sliderTargets.forEach(slider => {
      let type = slider.dataset.controls
      let relatedInput = document.getElementById(`pricing-calculator-${type}`)
      let breakdownVal = document.getElementById(`pricing-breakdown-${type}-credits`)
      let breakdownCost = document.getElementById(`pricing-breakdown-${type}-cost`)

      noUiSlider.create(slider, {
        start: relatedInput.value,
        step: 1000,
        connect: [true, false],
        format: {
          to: (v) => parseFloat(v).toFixed(0),
          from: (v) => parseFloat(v).toFixed(0)
        },
        range: {
          'min': [0],
          '30%': [10000, 1000],
          '65%': [100000, 50000],
          '99%': [1000000, 1],
          'max': [1000001]
        },
        pips: {
          mode: 'range',
          density: 0,
          filter: function (val, _type) {
            return [1000, 10000, 100000, 1000000].includes(val) ? 1 : -1
          },
          format: {
            to: (v) => this._formattingPipsNumbers(v),
            from: (v) => this._formattingPipsNumbers(v)
          },
        }
      })

      // Update input when updating the slider
      slider.noUiSlider.on("update", (values, handle) => {
        let sliderValue = values[handle]
        if (slider.dataset.controls === "searches") {
          this.currentSearchesValue = sliderValue
        } else if (slider.dataset.controls === "verifications") {
          this.currentVerificationsValue = sliderValue
        }

        // Update links parameters
        checkoutURL.searchParams.set(type, sliderValue)
        this.buyBtnTarget.href = checkoutURL.href

        salesURL.searchParams.set(type, sliderValue)
        this.contactSalesBtnTarget.href = salesURL.href

        // Update individual slider values
        if (sliderValue > 1000000) {
          relatedInput.value = `${App.numberWithCommas(1000000)}+`
          breakdownVal.textContent = `${App.numberWithCommas(1000000)}+`
        } else {
          relatedInput.value = `${App.numberWithCommas(sliderValue)}`
          breakdownVal.textContent = `${App.numberWithCommas(sliderValue)}`
        }

        // Toggle contact sales button
        if (this.currentSearchesValue === 0 && this.currentVerificationsValue === 0) {
          breakdownCost.textContent = CurrencyHelper.numberToCurrency(this._calculateCost(type, sliderValue), CurrencyHelper.currencySymbol(this.currencyValue))
          this.totalCostTarget.textContent = CurrencyHelper.numberToCurrency(parseFloat(this.element.dataset.searchesCost) + parseFloat(this.element.dataset.verificationsCost), CurrencyHelper.currencySymbol(this.currencyValue))
          this.contactSalesBtnTarget.classList.add("d-none")
          this.buyBtnTarget.classList.add("d-none")
          this.disabledBuyBtnTarget.classList.remove("d-none")
        } else if (this.currentSearchesValue > 1000000 || this.currentVerificationsValue > 1000000) {
          breakdownCost.textContent = i18n.t("js.front.pricing_calculator.custom")
          this.totalCostTarget.innerHTML = `<small>${i18n.t("js.front.pricing_calculator.custom_pricing")}</small>`
          this.buyBtnTarget.classList.add("d-none")
          this.disabledBuyBtnTarget.classList.add("d-none")
          this.contactSalesBtnTarget.classList.remove("d-none")
        } else {
          breakdownCost.textContent = CurrencyHelper.numberToCurrency(this._calculateCost(type, sliderValue), CurrencyHelper.currencySymbol(this.currencyValue))
          this.totalCostTarget.textContent = CurrencyHelper.numberToCurrency(parseFloat(this.element.dataset.searchesCost) + parseFloat(this.element.dataset.verificationsCost), CurrencyHelper.currencySymbol(this.currencyValue))
          this.contactSalesBtnTarget.classList.add("d-none")
          this.disabledBuyBtnTarget.classList.add("d-none")
          this.buyBtnTarget.classList.remove("d-none")
        }

        let discount = this._calculateCombinedNextTierSavings(
          this.currentSearchesValue,
          this.currentVerificationsValue
        )

        if (discount.showDiscount) {
          this.discountMessageTarget.innerHTML = discount.message
          this.discountTarget.classList.add("is-visible")
        } else {
          this.discountTarget.classList.remove("is-visible")
        }
      })

      // Update slider when updating the input
      relatedInput.addEventListener("blur", function () {
        slider.noUiSlider.set(Math.round(this.value / 1000) * 1000)
      })
    })
  }

  applyDiscount() {
    const searchesValue = parseInt(this.applyDiscountBtnTarget.dataset.searches)
    const verificationsValue = parseInt(this.applyDiscountBtnTarget.dataset.verifications)

    this.sliderTargets.forEach(slider => {
      if (slider.dataset.controls === "searches") {
        slider.noUiSlider.set(searchesValue)
      } else if (slider.dataset.controls === "verifications") {
        slider.noUiSlider.set(verificationsValue)
      }
    })
  }

  _getPricingData() {
    return {
      volumesPerType: {
        "searches": [1000, 10000, 20000, 50000, 150000, 250000, 500000, 1000000],
        "verifications": [1000, 20000, 50000, 100000, 250000, 500000, 1000000]
      },
      costsRange: {
        "gbp": {
          "verifications": [0.00935, 0.00765, 0.0068, 0.00391, 0.003145, 0.00306, 0.002448],
          "searches": [0.0425, 0.02169, 0.0161, 0.0115, 0.00782, 0.007548, 0.007344, 0.005875]
        },
        "usd": {
          "verifications": [0.011, 0.009, 0.008, 0.0046, 0.0037, 0.0036, 0.00288],
          "searches": [0.05, 0.026, 0.019, 0.014, 0.0092, 0.0089, 0.0086, 0.00691]
        },
        "eur": {
          "verifications": [0.011, 0.009, 0.008, 0.0046, 0.0037, 0.0036, 0.00288],
          "searches": [0.05, 0.026, 0.019, 0.014, 0.0092, 0.0089, 0.0086, 0.00691]
        }
      }
    }
  }

  _calculateCost(type, value) {
    const { volumesPerType, costsRange } = this._getPricingData()
    let currentCurrencyRanges = costsRange[this.currencyValue]
    let cost = currentCurrencyRanges[type][0]

    volumesPerType[type].forEach((_volume, index) => {
      if (value >= volumesPerType[type][index]) {
        cost = currentCurrencyRanges[type][index]
      }
    })

    this.element.dataset[`${type}Cost`] = cost * value
    return cost * value
  }

  _calculateNextTierSavings(type, currentValue) {
    const { volumesPerType, costsRange } = this._getPricingData()
    let currentCurrencyRanges = costsRange[this.currencyValue]
    let volumes = volumesPerType[type]

    // Find the next tier volume
    let nextTierVolume = null
    for (let i = 0; i < volumes.length; i++) {
      if (currentValue < volumes[i]) {
        nextTierVolume = volumes[i]
        break
      }
    }

    // If no next tier exists (already at maximum), return null
    if (!nextTierVolume) {
      return null
    }

    // Calculate current cost
    let currentCost = this._calculateCost(type, currentValue)

    // Calculate cost at next tier
    let nextTierCostPerUnit = null
    for (let i = 0; i < volumes.length; i++) {
      if (nextTierVolume >= volumes[i]) {
        nextTierCostPerUnit = currentCurrencyRanges[type][i]
      }
    }

    let nextTierTotalCost = nextTierVolume * nextTierCostPerUnit
    let savings = currentCost - nextTierTotalCost

    return {
      nextTierVolume: nextTierVolume,
      currentCost: currentCost,
      nextTierCost: nextTierTotalCost,
      savings: savings,
      savingsFormatted: CurrencyHelper.numberToCurrency(savings, CurrencyHelper.currencySymbol(this.currencyValue))
    }
  }

  _calculateCombinedNextTierSavings(searchValue, verificationValue) {
    // Skip if both values are 0 or above maximum tier
    if ((searchValue === 0 && verificationValue === 0) || searchValue > 1000000 || verificationValue > 1000000) {
      return { showDiscount: false, message: "" }
    }

    // Handle case where only one type has a value
    if (searchValue === 0 && verificationValue > 0) {
      let verificationSavings = this._calculateNextTierSavings("verifications", verificationValue)

      this.applyDiscountBtnTarget.dataset.searches = 0
      this.applyDiscountBtnTarget.dataset.verifications = verificationSavings.nextTierVolume

      if (!verificationSavings || verificationSavings.savings <= 0) {
        return { showDiscount: false, message: "" }
      }
      return {
        showDiscount: true,
        message: i18n.t("js.front.pricing_calculator.discount_verifications_html", {
          verifications: App.numberWithCommas(verificationSavings.nextTierVolume),
          savings: CurrencyHelper.currencySymbol(this.currencyValue) + Math.round(verificationSavings.savings)
        })
      }
    }

    if (verificationValue === 0 && searchValue > 0) {
      let searchSavings = this._calculateNextTierSavings("searches", searchValue)

      this.applyDiscountBtnTarget.dataset.searches = searchSavings.nextTierVolume
      this.applyDiscountBtnTarget.dataset.verifications = 0

      if (!searchSavings || searchSavings.savings <= 0) {
        return { showDiscount: false, message: "" }
      }
      return {
        showDiscount: true,
        message: i18n.t("js.front.pricing_calculator.discount_searches_html", {
          searches: App.numberWithCommas(searchSavings.nextTierVolume),
          savings: CurrencyHelper.currencySymbol(this.currencyValue) + Math.round(searchSavings.savings)
        })
      }
    }

    // Handle case where both types have values - find the best single upgrade
    let searchSavings = this._calculateNextTierSavings("searches", searchValue)
    let verificationSavings = this._calculateNextTierSavings("verifications", verificationValue)

    let currentSearchCost = this._calculateCost("searches", searchValue)
    let currentVerificationCost = this._calculateCost("verifications", verificationValue)
    let totalCurrentCost = currentSearchCost + currentVerificationCost

    let bestOption = null

    // Option 1: Upgrade only searches (keep current verifications)
    if (searchSavings && searchSavings.savings > 0) {
      let newTotalCost = searchSavings.nextTierCost + currentVerificationCost
      let totalSavings = totalCurrentCost - newTotalCost

      this.applyDiscountBtnTarget.dataset.searches = searchSavings.nextTierVolume
      this.applyDiscountBtnTarget.dataset.verifications = verificationValue

      if (totalSavings > 0) {
        bestOption = {
          savings: totalSavings,
          message: i18n.t("js.front.pricing_calculator.discount_searches_html", {
            searches: App.numberWithCommas(searchSavings.nextTierVolume),
            savings: CurrencyHelper.currencySymbol(this.currencyValue) + Math.round(totalSavings)
          })
        }
      }
    }

    // Option 2: Upgrade only verifications (keep current searches)
    if (verificationSavings && verificationSavings.savings > 0) {
      let newTotalCost = currentSearchCost + verificationSavings.nextTierCost
      let totalSavings = totalCurrentCost - newTotalCost

      this.applyDiscountBtnTarget.dataset.searches = searchValue
      this.applyDiscountBtnTarget.dataset.verifications = verificationSavings.nextTierVolume

      if (totalSavings > 0 && (!bestOption || totalSavings > bestOption.savings)) {
        bestOption = {
          savings: totalSavings,
          message: i18n.t("js.front.pricing_calculator.discount_verifications_html", {
            verifications: App.numberWithCommas(verificationSavings.nextTierVolume),
            savings: CurrencyHelper.currencySymbol(this.currencyValue) + Math.round(totalSavings)
          })
        }
      }
    }

    // Option 3: Upgrade both types
    if (searchSavings && verificationSavings && searchSavings.savings > 0 && verificationSavings.savings > 0) {
      let newTotalCost = searchSavings.nextTierCost + verificationSavings.nextTierCost
      let totalSavings = totalCurrentCost - newTotalCost

      this.applyDiscountBtnTarget.dataset.searches = searchSavings.nextTierVolume
      this.applyDiscountBtnTarget.dataset.verifications = verificationSavings.nextTierVolume

      if (totalSavings > 0 && (!bestOption || totalSavings > bestOption.savings)) {
        bestOption = {
          savings: totalSavings,
          message: i18n.t("js.front.pricing_calculator.discount_both_html", {
            searches: App.numberWithCommas(searchSavings.nextTierVolume),
            verifications: App.numberWithCommas(verificationSavings.nextTierVolume),
            savings: CurrencyHelper.currencySymbol(this.currencyValue) + Math.round(totalSavings)
          })
        }
      }
    }

    if (bestOption) {
      return {
        showDiscount: true,
        message: bestOption.message
      }
    }

    return { showDiscount: false, message: "" }
  }

  _formattingPipsNumbers(val) {
    const locale = i18n.locale
    const numberFormatter = new Intl.NumberFormat(locale, { notation: 'compact' })

    return val > 999999 ? numberFormatter.format(parseFloat(val).toFixed(0)) : App.numberWithCommas(parseFloat(val).toFixed(0))
  }
}
